import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Header } from '../common/Header';
import Card from '@mui/material/Card';
import TableGrid from '../table-grid/TableGrid';
import { EndPoints } from '../../axios/endPoints';
import { GridColDef } from '@mui/x-data-grid';
import {
    Button,
    Grid,
    IconButton,
    Tooltip,
    Box,
    Typography,
    CircularProgress,
    TextField,
    Divider,
} from '@mui/material';
import moment from 'moment';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { handleToggleFileDetailsPopup, handleToggleTrainingModelDetailsDialog } from '../../store/mlcreator-slice/mlcreator';
import TrainingModelDetails from '../ml-creator/TrainingModelDetails';
import {
    handleNewPrediction,
    handleRefreshSelectedMLTesterTable,
    handleToggleDeletePredictionResult,
    handleTogglePredictionResultDetailsDialog,
} from '../../store/mltester-slice/mltester';
import NewPredictionDialog from './NewPredictionDialog';
import SuccessfulPredictionDetails from './SuccessfulPredictionDetails';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import { DownloadFile } from '../../services/FileServices';
import DeletePredictionResultDialog from './DeletePredictionResultDialog';
import PredictionResultDetailsDialog from './PredictionResultDetailsDialog';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewTwoToneIcon from '@mui/icons-material/GridViewTwoTone';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { axiosInstance } from '../../axios/axiosHandler';
import qs from 'qs';
import { authHeader } from '../../store/auth-slice/auth-slice';
import { useDebounce } from 'use-debounce';
import { useDisplaySize } from '../../utils/useDisplaySize';
import { classificationRegressionTextColors } from '../../utils/useTextColors';
import TruncatedText from '../common/TruncatedText';
import TruncatedChip from '../common/TruncatedChip';
import TesterFileDetails from './TesterFileDetails';
import TesterFileData from './TesterFileData';

interface SelectedMLTesterProps { }

type ViewMode = 'table' | 'cards';

const SelectedMLTester = React.forwardRef<HTMLDivElement, SelectedMLTesterProps>((props, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const selectedTesterRow = useAppSelector((state: RootState) => state.mltester.selectedTesterRow);
    const refreshSelectedMLTesterTable = useAppSelector((state: RootState) => state.mltester.refreshSelectedMLTesterTable);
    const { cardsPerPage } = useDisplaySize();

    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const saved = window.localStorage.getItem('selectedMLTesterViewMode');
        return saved === 'table' ? 'table' : 'cards';
    });
    const [searchInput, setSearchInput] = useState<string>('');
    const [debouncedSearch] = useDebounce(searchInput, 500);
    const [cardPageIndex, setCardPageIndex] = useState<number>(0);
    const [cardData, setCardData] = useState<any[]>([]);
    const [cardLoading, setCardLoading] = useState<boolean>(false);
    const [cardTotalCount, setCardTotalCount] = useState<number>(0);
    const [cardHasNext, setCardHasNext] = useState<boolean>(false);
    const [cardHasPrevious, setCardHasPrevious] = useState<boolean>(false);

    const memoizedSelectedTesterRow = useMemo(() => selectedTesterRow, [selectedTesterRow]);

    const columns: GridColDef[] = useMemo(
        () => [
            { field: 'id', headerName: t('SELECTED_TESTER_ID_FIELD') },
            { field: 'name', headerName: t('SELECTED_TESTER_RESULT_NAME_FIELD'), minWidth: 225 },
            {
                field: 'trainedModelName',
                headerName: t('SELECTED_TESTER_MODEL_NAME_FIELD'),
                minWidth: 225,
                renderCell: (params) => (
                    <div
                        className="styled-div"
                        onClick={() =>
                            dispatch(
                                handleToggleTrainingModelDetailsDialog({
                                    isOpen: true,
                                    body: {
                                        id: params.row.trainedModelId,
                                        name: params.row.trainedModelName,
                                    },
                                })
                            )
                        }
                    >
                        {params.value}
                    </div>
                ),
            },
            {
                field: 'trainedModelSelectedTypeString',
                headerName: t('SELECTED_TESTER_MODEL_TYPE_FIELD'),
                minWidth: 150,
                renderCell: (params) => t(params.value),
            },
            { field: 'trainedModelSelectedName', headerName: t('SELECTED_TESTER_MODEL_SUBTYPE_FIELD'), minWidth: 200 },
            {
                field: 'trainedModelTrainingDatasetName',
                headerName: t('SELECTED_TESTER_TRAINING_DATASET_NAME_FIELD'),
                minWidth: 225,
                renderCell: (params) => (
                    <div
                        className="styled-div"
                        onClick={() => {
                            dispatch(
                                handleToggleFileDetailsPopup({
                                    open: true,
                                    body: {
                                        fileId: params.row.trainedModelTrainingDatasetId,
                                        fileName: params.row.trainedModelTrainingDatasetName,
                                    },
                                })
                            );
                        }}
                    >
                        {params.value}
                    </div>
                ),
            },
            {
                field: 'testingDate',
                headerName: t('SELECTED_TESTER_TESTING_DATE_FIELD'),
                minWidth: 175,
                valueFormatter: (params) => moment.utc(params.value).local().format('DD/MM/YYYY HH:mm'),
            },
            {
                field: 'actions',
                headerName: t('TESTER_ACTIONS_FIELD'),
                minWidth: 300,
                renderCell: (params) => <TesterActionsCell params={params} dispatch={dispatch} t={t} />,
            },
        ],
        [dispatch, t]
    );

    const fetchCardPage = useCallback(async () => {
        if (!memoizedSelectedTesterRow) return;
        setCardLoading(true);
        try {
            const queryObj = {
                datasetId: memoizedSelectedTesterRow.id,
                currentPage: cardPageIndex + 1,
                pageSize: cardsPerPage,
                searchText: debouncedSearch,
                sortColumns: [{ name: 'id', sortDirection: 'asc' }],
            };
            const response = await axiosInstance.get(EndPoints.ML_TESTER_GET_PREDICTION_RESULTS, {
                headers: authHeader().headers,
                params: queryObj,
                paramsSerializer: (params) => qs.stringify(params, { encodeValuesOnly: true, allowDots: true }),
            });
            setCardData(response.data.items);
            setCardTotalCount(response.data.totalCount);
            setCardHasNext(response.data.hasNext);
            setCardHasPrevious(response.data.hasPrevious);
        } catch (err) {
            console.error('Error fetching card page:', err);
            setCardData([]);
            setCardTotalCount(0);
        } finally {
            setCardLoading(false);
        }
    }, [memoizedSelectedTesterRow, cardPageIndex, debouncedSearch, cardsPerPage]);

    useEffect(() => {
        if (viewMode === 'cards' && memoizedSelectedTesterRow) {
            fetchCardPage();
        }
    }, [viewMode, memoizedSelectedTesterRow, cardPageIndex, refreshSelectedMLTesterTable, fetchCardPage]);

    useEffect(() => {
        setCardPageIndex(0);
    }, [memoizedSelectedTesterRow, cardsPerPage]);

    const handleToggleToTable = useCallback(() => {
        setViewMode('table');
        window.localStorage.setItem('selectedMLTesterViewMode', 'table');
    }, []);

    const handleToggleToCards = useCallback(() => {
        setViewMode('cards');
        window.localStorage.setItem('selectedMLTesterViewMode', 'cards');
        setCardPageIndex(0);
    }, []);

    const handleRefreshClick = useCallback(() => {
        dispatch(handleRefreshSelectedMLTesterTable());
    }, [dispatch]);

    const handleNewPredictionClick = useCallback(() => {
        dispatch(handleNewPrediction({ isOpen: true }));
    }, [dispatch]);

    const totalCardPages = Math.ceil(cardTotalCount / cardsPerPage);

    const handleNextPage = useCallback(() => {
        if (cardHasNext) {
            setCardPageIndex((prev) => prev + 1);
        }
    }, [cardHasNext]);

    const handlePrevPage = useCallback(() => {
        if (cardHasPrevious) {
            setCardPageIndex((prev) => prev - 1);
        }
    }, [cardHasPrevious]);

    return (
        <React.Fragment>
            {memoizedSelectedTesterRow ? (
                <Card ref={ref}>
                    <Header
                        title={t('SELECTED_TESTER_ROW', { name: memoizedSelectedTesterRow.name })}
                        helperIcon={<RedirectIconButton to={'/help/6'} className="helper-icon"><ContactSupportIcon /></RedirectIconButton>}
                    />
                    <Box sx={{ p: 2 }}>
                        <SelectedMLTesterToolbar
                            viewMode={viewMode}
                            onNewPrediction={handleNewPredictionClick}
                            onRefresh={handleRefreshClick}
                            onToggleToTable={handleToggleToTable}
                            onToggleToCards={handleToggleToCards}
                            onSearch={setSearchInput}
                        />
                        {viewMode === 'table' ? (
                            <TableView
                                columns={columns}
                                refresh={refreshSelectedMLTesterTable}
                                datasetId={memoizedSelectedTesterRow.id}
                                searchText={debouncedSearch}
                            />
                        ) : (
                            <Box
                                sx={{
                                    mt: 2,
                                    height: { xs: 300, sm: 375 },
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    justifyContent: 'center',
                                }}
                            >
                                <PredictionCardCarousel
                                    cardData={cardData}
                                    cardLoading={cardLoading}
                                    totalCardPages={totalCardPages}
                                    hasNext={cardHasNext}
                                    hasPrevious={cardHasPrevious}
                                    cardPageIndex={cardPageIndex}
                                    onNextPage={handleNextPage}
                                    onPrevPage={handlePrevPage}
                                    dispatch={dispatch}
                                    t={t}
                                />
                            </Box>
                        )}
                    </Box>
                    <TesterFileDetails />
                    <TesterFileData />
                    <NewPredictionDialog />
                    <TrainingModelDetails />
                    <SuccessfulPredictionDetails />
                    <DeletePredictionResultDialog />
                    <PredictionResultDetailsDialog />
                </Card>
            ) : null}
        </React.Fragment>
    );
});

interface SelectedMLTesterToolbarProps {
    viewMode: ViewMode;
    onNewPrediction: () => void;
    onRefresh: () => void;
    onToggleToTable: () => void;
    onToggleToCards: () => void;
    onSearch: (text: string) => void;
}

const SelectedMLTesterToolbar: React.FC<SelectedMLTesterToolbarProps> = React.memo((props) => {
    const { t } = useTranslation();
    const { viewMode, onNewPrediction, onRefresh, onToggleToTable, onToggleToCards, onSearch } = props;
    const [searchInput, setSearchInput] = useState<string>('');

    useEffect(() => {
        onSearch(searchInput);
    }, [searchInput, onSearch]);

    return (
        <Grid container alignItems="center" spacing={1}>
            <Grid item xs={12} sm={6}>
                <Button onClick={onNewPrediction} variant="contained" size="small" className="button-color">
                    {t('ML_TESTER_NEW_PREDICTION_BUTTON_TEXT')}
                </Button>
                <Tooltip title={t('REFRESH_TABLE_BUTTON_TOOLTIP')}>
                    <IconButton onClick={onRefresh} sx={{ ml: 1, color: '#555' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, alignItems: 'center' }}>
                <Box sx={{ mr: 2, width: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder={t('SELECTED_ML_TESTER_TOOLBAR_SEARCH')}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 200 } }}
                    />
                </Box>
                <Tooltip title={t('SELECTED_ML_TESTER_TOOLBAR_SHOW_AS_TABLE')}>
                    <IconButton onClick={onToggleToTable} sx={{ color: viewMode === 'table' ? 'var(--link-text-color)' : '#888' }}>
                        <TableRowsIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('SELECTED_ML_TESTER_TOOLBAR_SHOW_AS_CARDS')}>
                    <IconButton onClick={onToggleToCards} sx={{ ml: 1, color: viewMode === 'cards' ? 'var(--link-text-color)' : '#888' }}>
                        <GridViewTwoToneIcon />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );
});
SelectedMLTesterToolbar.displayName = 'SelectedMLTesterToolbar';

interface TableViewProps {
    columns: GridColDef[];
    refresh: number;
    datasetId: string;
    searchText: string;
}

const TableView: React.FC<TableViewProps> = React.memo((props) => {
    const { columns, refresh, datasetId, searchText } = props;
    return (
        <Box sx={{ mt: 2, height: { xs: 300, sm: 400, md: 500 }, width: '100%' }}>
            <TableGrid
                baseURL={EndPoints.ML_TESTER_GET_PREDICTION_RESULTS}
                aditionalQueryParams={{ datasetId }}
                columns={columns}
                sortColumns={[{ field: 'id', sort: 'asc' }]}
                refresh={refresh}
                disableRowSelection
                tableHeight={500}
                searchText={searchText}
                rowHeight={35}
                columnHeaderHeight={40}
            />
        </Box>
    );
});
TableView.displayName = 'TableView';

interface TesterActionsCellProps {
    params: any;
    dispatch: any;
    t: any;
}

const TesterActionsCell: React.FC<TesterActionsCellProps> = React.memo((props) => {
    const { params, dispatch, t } = props;

    return (
        <Grid container spacing={3} display="flex" justifyContent="center">
            <Grid item>
                <Tooltip title={t('TESTER_DETAILS_ACTION')}>
                    <IconButton
                        onClick={() =>
                            dispatch(
                                handleTogglePredictionResultDetailsDialog({
                                    isOpen: true,
                                    body: { id: params.row.id, name: params.row.name },
                                })
                            )
                        }
                    >
                        <InfoTwoToneIcon className="info-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item>
                <Tooltip title={t('TESTER_DOWNLOAD_ACTION')}>
                    <IconButton
                        onClick={() => {
                            DownloadFile(EndPoints.ML_TESTER_DOWNLOAD_PREDICTION_RESULT + `?id=${params.row.id}`);
                        }}
                    >
                        <CloudDownloadTwoToneIcon className="download-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item>
                <Tooltip title={t('TESTER_DELETE_ACTION')}>
                    <IconButton
                        onClick={() =>
                            dispatch(
                                handleToggleDeletePredictionResult({
                                    isOpen: true,
                                    body: { id: params.row.id, name: params.row.name },
                                })
                            )
                        }
                    >
                        <DeleteForeverTwoToneIcon className="delete-icon" />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );
});
TesterActionsCell.displayName = 'TesterActionsCell';

interface PredictionCardItemProps {
    prediction: any;
    dispatch: any;
    t: any;
}

const PredictionCardItem: React.FC<PredictionCardItemProps> = React.memo((props) => {
    const { prediction, dispatch, t } = props;

    const { color, backgroundColor } = classificationRegressionTextColors[prediction.trainedModelSelectedTypeString] || { color: '#333', backgroundColor: '#f0f0f0' };

    return (
        <Card
            sx={{
                flex: '1 0 0',
                p: { xs: 2, sm: 3 },
                mb: 2,
                borderRadius: 2,
                backgroundColor: '#fafafa',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: 1,
                border: '1px solid rgba(0, 0, 0, 0.15)',
                maxWidth: '100%',
                width: '100%',
            }}
        >
            <Box sx={{
                overflowY: 'auto',
                minWidth: 0,
            }}>
                <TruncatedText
                    text={prediction.name || ''}
                    maxLength={20}
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: 'var(--link-text-color)',
                    }}
                />

                <TruncatedText
                    text={prediction.id || ''}
                    maxLength={35}
                    sx={{ mb: 1, color: '#333' }}
                >
                    <strong>{t('SELECTED_TESTER_ID_FIELD')}:</strong>{' '}
                </TruncatedText>

                <TruncatedText
                    text={prediction.trainedModelName || ''}
                    maxLength={15}
                    sx={{ mb: 0.5, color: '#333', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() =>
                        dispatch(
                            handleToggleTrainingModelDetailsDialog({
                                isOpen: true,
                                body: {
                                    id: prediction.trainedModelId,
                                    name: prediction.trainedModelName,
                                },
                            })
                        )
                    }
                >
                    <strong>{t('SELECTED_TESTER_MODEL_NAME_FIELD')}:</strong>{' '}
                </TruncatedText>

                <Box sx={{ mb: 0.5 }}>
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{ color: '#333', fontWeight: 'bold' }}
                    >
                        {t('SELECTED_TESTER_MODEL_TYPE_FIELD')}:
                    </Typography>
                    {' '}
                    <TruncatedChip
                        label={t(prediction.trainedModelSelectedTypeString) || ''}
                        maxLength={15}
                        sx={{ color, backgroundColor }}
                    />
                </Box>

                <TruncatedText
                    text={prediction.trainedModelSelectedName || ''}
                    maxLength={12}
                    sx={{ mb: 0.5, color: '#333' }}
                >
                    <strong>{t('SELECTED_TESTER_MODEL_SUBTYPE_FIELD')}:</strong>{' '}
                </TruncatedText>

                <TruncatedText
                    text={prediction.trainedModelTrainingDatasetName || ''}
                    maxLength={13}
                    sx={{ mb: 0.5, color: '#333', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => {
                        dispatch(
                            handleToggleFileDetailsPopup({
                                open: true,
                                body: {
                                    fileId: prediction.trainedModelTrainingDatasetId,
                                    fileName: prediction.trainedModelTrainingDatasetName,
                                },
                            })
                        );
                    }}
                >
                    <strong>{t('SELECTED_TESTER_TRAINING_DATASET_NAME_FIELD')}:</strong>{' '}
                </TruncatedText>

                <Typography
                    variant="body2"
                    sx={{
                        mb: 0.5,
                        color: '#333',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                    }}
                >
                    <strong>{t('SELECTED_TESTER_TESTING_DATE_FIELD')}:</strong>{' '}
                    {moment.utc(prediction.testingDate).local().format('DD/MM/YYYY HH:mm')}
                </Typography>
            </Box>

            <Divider sx={{ mt: 2 }} />
            <Box sx={{ mt: 2 }}>
                <TesterActionsCell params={{ row: prediction }} dispatch={dispatch} t={t} />
            </Box>
        </Card>
    );
});

PredictionCardItem.displayName = 'PredictionCardItem';

interface PredictionCardCarouselProps {
    cardData: any[];
    cardLoading: boolean;
    totalCardPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
    cardPageIndex: number;
    onNextPage: () => void;
    onPrevPage: () => void;
    dispatch: any;
    t: any;
}

const PredictionCardCarousel: React.FC<PredictionCardCarouselProps> = (props) => {
    const { cardData, cardLoading, totalCardPages, hasNext, hasPrevious, cardPageIndex, onNextPage, onPrevPage, dispatch, t } = props;
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);

    const showLoading = useMemo(() => cardLoading, [cardLoading]);
    const showEmpty = useMemo(() => !cardLoading && cardData.length === 0, [cardLoading, cardData.length]);
    
    useEffect(() => {
        if (cardLoading) {
            setIsTransitioning(true);
            setFadeIn(false);
        } else {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
                setFadeIn(true);
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [cardLoading]);

    const stableCardData = useMemo(() => cardData, [cardData]);
    
    if (showLoading || isTransitioning) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    opacity: showLoading ? 0.8 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                    transform: 'translateZ(0)',
                }}
            >
                <CircularProgress 
                    style={{ 
                        color: 'var(--button-background-color)',
                        transform: 'translateZ(0)', 
                    }} 
                />
            </Box>
        );
    }
    
    if (showEmpty) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    opacity: fadeIn ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    transform: 'translateZ(0)',
                }}
            >
                <Typography 
                    variant="body2" 
                    sx={{ 
                        textAlign: 'center', 
                        color: '#666',
                        transform: 'translateZ(0)',
                    }}
                >
                    {t('PREDICTION_CARD_CAROUSEL_NO_DATA_AVAILABLE')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
            <IconButton
                onClick={onPrevPage}
                sx={{ color: totalCardPages > 1 ? 'var(--link-text-color)' : '#ccc' }}
                disabled={totalCardPages <= 1 || !hasPrevious}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    height: '100%',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: '16px',
                        justifyContent: 'center',
                        height: '100%',
                    }}
                >
                    {stableCardData.map((prediction) => (
                        <PredictionCardItem key={prediction.id} prediction={prediction} dispatch={dispatch} t={t} />
                    ))}
                </div>
            </Box>
            <IconButton
                onClick={onNextPage}
                sx={{ color: totalCardPages > 1 ? 'var(--link-text-color)' : '#ccc' }}
                disabled={totalCardPages <= 1 || !hasNext}
            >
                <ChevronRightIcon />
            </IconButton>
        </Box>
    );
};

export default React.memo(SelectedMLTester);